import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebaseConfig';

// Membuat context untuk autentikasi
const AuthContext = createContext();

// Provider untuk menyediakan state autentikasi
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Cek status autentikasi saat aplikasi dimuat
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    // Membersihkan listener saat komponen dilepas
    return () => unsubscribe();
  }, []);

  // Fungsi login
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Jika login berhasil, arahkan ke dashboard
      navigate('/admin/dashboard');
      return { success: true, user };
    } catch (error) {
      let errorMessage = 'Login gagal';

      // Menangani berbagai jenis error dari Firebase
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'Akun tidak ditemukan';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Password salah';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Email tidak valid';
          break;
        case 'auth/user-disabled':
          errorMessage = 'Akun telah dinonaktifkan';
          break;
        default:
          errorMessage = error.message || 'Terjadi kesalahan saat login';
      }

      return { success: false, message: errorMessage };
    }
  };

  // Fungsi logout
  const logout = async () => {
    try {
      await signOut(auth);
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Fungsi untuk mengecek apakah user sudah login
  const isAuthenticated = !!user;

  // Value yang akan disediakan oleh context
  const value = {
    user,
    login,
    logout,
    isAuthenticated,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook untuk menggunakan auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};